/**
 * LoginScreen — pantalla de entrada cuando user === null && !loading.
 *
 * Usa Google Sign-In via la lib `google.accounts.id` ya cargada en index.html.
 * Renderizamos el botón oficial dentro de un div ref. El callback recibe el
 * id_token (JWT firmado por Google), que pasamos a /aetherlite/auth/google
 * a través de useAuth().loginGoogle.
 */
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../lib/toast";

export function LoginScreen() {
  const { loginGoogle } = useAuth();
  const btnRef = useRef<HTMLDivElement>(null);
  const [loggingIn, setLoggingIn] = useState(false);
  const [scriptReady, setScriptReady] = useState(
    typeof window !== "undefined" && Boolean(window.google?.accounts?.id),
  );
  const toast = useToast();

  // Espera a que el script de Google se cargue (es <script async defer>).
  useEffect(() => {
    if (scriptReady) return;
    const t = window.setInterval(() => {
      if (window.google?.accounts?.id) {
        setScriptReady(true);
        window.clearInterval(t);
      }
    }, 150);
    return () => window.clearInterval(t);
  }, [scriptReady]);

  // Inicializa + renderiza el botón cuando el script está listo.
  useEffect(() => {
    if (!scriptReady) return;
    const clientId = window.GOOGLE_CLIENT_ID || import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.error("AetherLite: GOOGLE_CLIENT_ID no configurado en index.html.");
      return;
    }
    if (!btnRef.current) return;

    window.google!.accounts.id.initialize({
      client_id: clientId,
      callback: async (response: { credential: string }) => {
        try {
          setLoggingIn(true);
          await loginGoogle(response.credential);
        } catch (e) {
          console.error("Login Google falló:", e);
          toast(e instanceof Error ? e.message : "No pudimos iniciar sesión. Reintentá.");
          setLoggingIn(false);
        }
      },
      auto_select: false,
      ux_mode: "popup",
    });

    // Limpieza por re-render: vaciamos el contenedor antes de re-render.
    btnRef.current.innerHTML = "";
    window.google!.accounts.id.renderButton(btnRef.current, {
      type: "standard",
      theme: "outline",
      size: "large",
      text: "continue_with",
      shape: "pill",
      logo_alignment: "left",
      width: 280,
    });
  }, [scriptReady, loginGoogle, toast]);

  return (
    <div className="page" style={{
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      padding: 24,
      background:
        "radial-gradient(circle at 20% 10%, color-mix(in srgb, var(--primary) 15%, transparent) 0%, transparent 60%)," +
        "radial-gradient(circle at 80% 90%, color-mix(in srgb, var(--accent-magenta) 15%, transparent) 0%, transparent 55%)," +
        "var(--bg)",
    }}>
      <div style={{
        width: "min(420px, 100%)",
        background: "var(--bg-elev)",
        border: "1px solid var(--border)",
        borderRadius: 20,
        padding: "36px 28px",
        boxShadow: "0 12px 40px rgba(15, 23, 42, 0.08)",
        textAlign: "center",
      }}>
        <div className="brand-mark" style={{
          width: 64, height: 64,
          fontSize: 26,
          fontFamily: "var(--font-display)",
          margin: "0 auto 20px",
        }}>AL</div>

        <h1 style={{
          fontFamily: "var(--font-display)",
          fontSize: 28,
          fontWeight: 800,
          margin: "0 0 8px",
          color: "var(--fg)",
        }}>
          Bienvenido a AetherLite
        </h1>

        <p style={{ color: "var(--fg-muted)", fontSize: 15, lineHeight: 1.5, margin: "0 0 28px" }}>
          Editor web para entrenadores personales. Crea planes profesionales con
          1.408 ejercicios animados y compártelos con tus clientes.
        </p>

        <div ref={btnRef} style={{
          display: "flex",
          justifyContent: "center",
          minHeight: 44,
          opacity: loggingIn ? 0.5 : 1,
          pointerEvents: loggingIn ? "none" : "auto",
        }} />

        {!scriptReady && (
          <p className="tiny muted" style={{ marginTop: 12 }}>
            Cargando inicio de sesión…
          </p>
        )}

        {loggingIn && (
          <p className="tiny muted" style={{ marginTop: 12 }}>
            Validando con Google…
          </p>
        )}

        <p style={{
          marginTop: 28,
          fontSize: 12,
          color: "var(--fg-faint)",
        }}>
          Una iniciativa de{" "}
          <a href="https://nilabs.co" target="_blank" rel="noopener noreferrer"
            style={{ color: "var(--primary)", textDecoration: "none", fontWeight: 600 }}>
            Nilabs
          </a>
        </p>
      </div>
    </div>
  );
}
